/* eslint-disable no-restricted-syntax, no-continue, no-await-in-loop */
import offersService from './offersService';
import extrasService from './extrasService';
// packagesService removed — this service validates stock only now

/**
 * Service centralisé pour valider le stock et les prix
 * Utilisé par toutes les étapes du tunnel
 */

const bookingValidationService = {
  /**
   * Valide un STAGE
   * Retourne: { available, priceChanged, oldPrice, newPrice, productId, type: 'stage' }
   */
  async validateStage(stage, weekIds) {
    try {
      const offers = await offersService.getOffers();
      const allOffers = offers?.data?.offers || [];

      // Chercher les offers correspondant aux weeks sélectionnées
      const currentOffers = allOffers.filter(
        (o) => o.packageId === stage.packageId && weekIds.includes(o.weekId),
      );

      // Vérifier disponibilité / rupture (flag `SOLD_OUT`)
      const hasSoldOut = currentOffers.some((o) => o.flag === 'SOLD_OUT');
      const allWeeksFound = weekIds.every((wId) => currentOffers.some((o) => o.weekId === wId));

      if (currentOffers.length === 0 || hasSoldOut || !allWeeksFound) {
        return {
          available: false,
          productId: stage.packageId,
          type: 'stage',
          reason: 'Stage épuisé',
        };
      }

      return {
        available: true,
        productId: stage.packageId,
        type: 'stage',
      };
    } catch (error) {
      console.error('Erreur validation stage:', error);
      return {
        available: true,
        productId: stage.packageId,
        type: 'stage',
      };
    }
  },

  /**
   * Valide une OPTION (Extra)
   * Retourne: { available, priceChanged, oldPrice, newPrice, productId, type: 'option' }
   */
  async validateOption(option, playerCount, weekIds) {
    try {
      const stocks = await extrasService.getStocks(weekIds, 2); // familyId=2 pour options
      const productStocks = stocks?.data?.productsStocks || [];

      const productStock = productStocks.find((ps) => ps.productId === option.productId);

      if (!productStock) {
        console.warn(`Stock non trouvé pour option ${option.id} (productId: ${option.productId})`);
        return {
          available: false,
          productId: option.id,
          type: 'option',
          reason: 'Option indisponible',
        };
      }

      // Vérifier la disponibilité : stock - joueurs >= 0
      const stockAvailable = productStock.stock - playerCount >= 0;

      if (!stockAvailable) {
        return {
          available: false,
          productId: option.id,
          type: 'option',
          reason: `Option en rupture de stock (${productStock.stock} disponible, ${playerCount} demandé)`,
        };
      }

      return {
        available: true,
        productId: option.id,
        type: 'option',
      };
    } catch (error) {
      console.error('Erreur validation option:', error);
      return {
        available: true,
        productId: option.id,
        type: 'option',
      };
    }
  },

  /**
   * Valide un HÉBERGEMENT
   * Retourne: { available, priceChanged, oldPrice, newPrice, productId, type: 'accommodation' }
   */
  async validateAccommodation(accommodation, quantity, weekIds) {
    try {
      if (!weekIds || weekIds.length === 0) {
        console.warn('Pas de weekIds pour valider l\'hébergement');
        return {
          available: true,
          productId: accommodation.id,
          type: 'accommodation',
        };
      }

      const stocks = await extrasService.getStocks(weekIds, 3); // familyId=3 pour hébergement
      const productStocks = stocks?.data?.productsStocks || [];

      if (!productStocks.length) {
        console.warn('Pas de stock retourné pour hébergement', { weekIds, quantity });
        return {
          available: true,
          productId: accommodation.id,
          type: 'accommodation',
        };
      }

      const accProductId = accommodation.productId || accommodation.product?.id;
      const accStock = productStocks.find((ps) => ps.productId === accProductId);

      if (!accStock || accStock.stock < quantity) {
        return {
          available: false,
          productId: accommodation.id,
          type: 'accommodation',
          reason: `Hébergement indisponible (${quantity} demandé, ${accStock?.stock || 0} disponible)`,
        };
      }

      return {
        available: true,
        productId: accommodation.id,
        type: 'accommodation',
      };
    } catch (error) {
      console.error('Erreur validation accommodation:', error);
      return {
        available: true,
        productId: accommodation.id,
        type: 'accommodation',
      };
    }
  },

  /**
   * Validation COMPLÈTE du panier
   * Retourne: { isValid, errors: [], priceChanges: [], unavailableProducts: [] }
   */
  async validateCompleteCart(players, hosting) {
    const validationResults = {
      isValid: true,
      errors: [],
      priceChanges: [],
      unavailableProducts: [],
    };

    // --- STAGE validation (stock checks) ---
    if (players?.length) {
      // Count usage per packageId+weekId
      const stageUsageCount = {};
      players.forEach((player) => {
        if (player.stage) {
          const weekIds = player.stage.weeks || [];
          weekIds.forEach((weekId) => {
            const key = `${player.stage.packageId}_${weekId}`;
            stageUsageCount[key] = (stageUsageCount[key] || 0) + 1;
          });
        }
      });

      // If duplicates, fetch real stocks for impacted weeks
      const duplicatedStageKeys = Object.entries(stageUsageCount).filter(([, count]) => count > 1);
      const stageStocks = {};
      if (duplicatedStageKeys.length > 0) {
        const allWeekIds = [...new Set(players.flatMap((p) => p.stage?.weeks || []))];
        try {
          const stocksResponse = await extrasService.getStocks(allWeekIds, 1);
          const productStocks = stocksResponse?.data?.productsStocks || [];
          productStocks.forEach((ps) => { stageStocks[ps.productId] = ps.stock; });
        } catch (e) {
          console.warn('[CartValidation] Impossible de récupérer les stocks stages:', e);
        }
      }

      // Options aggregated usage
      const optionUsageCount = {};
      players.forEach((player) => {
        const allOptions = [...(player.options || []), ...(player.otherOptions || [])];
        allOptions.forEach((option) => {
          if (option.productId) {
            const qty = option.quantities?.reduce((sum, q) => sum + (q.quantity || 0), 0) || 1;
            optionUsageCount[option.productId] = (optionUsageCount[option.productId] || 0) + qty;
          }
        });
      });

      const duplicatedOptionProductIds = Object.entries(optionUsageCount).filter(([, c]) => c > 1).map(([p]) => p);
      const optionStocks = {};
      if (duplicatedOptionProductIds.length > 0) {
        const allWeekIds = [...new Set(players.flatMap((p) => p.stage?.weeks || []))];
        try {
          const stocksResponse = await extrasService.getStocks(allWeekIds, 2);
          const productStocks = stocksResponse?.data?.productsStocks || [];
          productStocks.forEach((ps) => { optionStocks[ps.productId] = ps.stock; });
        } catch (e) {
          console.warn('[CartValidation] Impossible de récupérer les stocks options:', e);
        }
      }

      const optionValidatedCount = {};
      const stageValidatedCount = {};

      for (const player of players) {
        if (!player.stage) continue;

        const stageWeekIds = player.stage.weeks || [];

        // intra-cart stage stock check
        let exceededStock = false;
        for (const weekId of stageWeekIds) {
          const key = `${player.stage.packageId}_${weekId}`;
          stageValidatedCount[key] = (stageValidatedCount[key] || 0) + 1;
          const stock = stageStocks[player.stage.productId];
          if (stock !== undefined && stageValidatedCount[key] > stock) {
            exceededStock = true;
            break;
          }
        }

        if (exceededStock) {
          validationResults.isValid = false;
          validationResults.errors.push({
            type: 'stage',
            playerId: player.id,
            productId: player.stage.packageId,
            message: 'Stage épuisé (stock insuffisant pour tous les joueurs)',
            severity: 'danger',
          });
          validationResults.unavailableProducts.push(`stage_${player.id}_${player.stage.packageId}`);
          continue;
        }

        const stageValidation = await this.validateStage(player.stage, stageWeekIds);
        if (!stageValidation.available) {
          validationResults.isValid = false;
          validationResults.errors.push({
            type: 'stage',
            playerId: player.id,
            productId: stageValidation.productId,
            message: stageValidation.reason,
            severity: 'danger',
          });
          validationResults.unavailableProducts.push(`stage_${player.id}_${stageValidation.productId}`);
        }

        const validatePlayerOptions = async (options) => {
          if (!options?.length) return;
          for (const option of options) {
            const totalQuantity = option.quantities?.reduce((sum, q) => sum + (q.quantity || 0), 0) || 1;

            if (option.productId && optionStocks[option.productId] !== undefined) {
              optionValidatedCount[option.productId] = (optionValidatedCount[option.productId] || 0) + totalQuantity;
              if (optionValidatedCount[option.productId] > optionStocks[option.productId]) {
                validationResults.isValid = false;
                validationResults.errors.push({
                  type: 'option',
                  playerId: player.id,
                  productId: option.id,
                  message: `Option en rupture de stock (stock: ${optionStocks[option.productId]},`
                    + ` total panier: ${optionValidatedCount[option.productId]})`,
                  severity: 'danger',
                });
                validationResults.unavailableProducts.push(`option_${player.id}_${option.id}`);
                continue;
              }
            }

            const optionValidation = await this.validateOption(option, totalQuantity, stageWeekIds);
            if (!optionValidation.available) {
              validationResults.isValid = false;
              validationResults.errors.push({
                type: 'option',
                playerId: player.id,
                productId: optionValidation.productId,
                message: optionValidation.reason,
                severity: 'danger',
              });
              validationResults.unavailableProducts.push(`option_${player.id}_${optionValidation.productId}`);
            }
          }
        };

        await validatePlayerOptions(player.options);
        await validatePlayerOptions(player.otherOptions);
      }
    }

    // --- ACCOMMODATION validation (stock checks) ---
    if (hosting?.length) {
      const allWeekIds = [];
      if (players?.length) {
        players.forEach((player) => {
          if (player.stage?.weeks?.length) allWeekIds.push(...player.stage.weeks);
        });
      }
      const uniqueWeekIds = [...new Set(allWeekIds)];

      for (const accommodation of hosting) {
        const accValidation = await this.validateAccommodation(accommodation, accommodation.quantity, uniqueWeekIds);
        if (!accValidation.available) {
          validationResults.isValid = false;
          validationResults.errors.push({
            type: 'accommodation',
            productId: accValidation.productId,
            message: accValidation.reason,
            severity: 'danger',
          });
          validationResults.unavailableProducts.push(accValidation.productId);
        }
      }
    }

    return validationResults;
  },

  // Price calculation helpers removed — price logic is handled elsewhere
};

export default bookingValidationService;
